import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Plug, Scale, Calendar, Zap, Briefcase, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'
import { PageHeader } from '@/components/dashboard-ui'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import api from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { useBrand, splitBrandName } from '@/hooks/useBrand'

interface PlannedIntegration {
  icon: React.ReactNode
  name: string
  description: string
  category: string
}

interface NylasStatus {
  connected: boolean
  status?: string
  provider?: string
  email?: string | null
  lastSyncAt?: string | null
  lastError?: string | null
}

const PROVIDER_LABEL: Record<string, string> = {
  google: 'Google via Nylas',
  microsoft: 'Microsoft 365 / Outlook',
  icloud: 'Apple iCloud',
  ews: 'Microsoft Exchange',
  imap: 'IMAP / CalDAV',
}

const getPlannedIntegrations = (appBaseName: string): PlannedIntegration[] => [
  {
    icon: <Scale className="h-6 w-6 text-blue-600" />,
    name: 'Clio',
    description: 'Sync contacts, matters, and call notes directly to your Clio account.',
    category: 'Practice Management',
  },
  {
    icon: <Briefcase className="h-6 w-6 text-indigo-600" />,
    name: 'MyCase',
    description: 'Push new leads and intake data into MyCase automatically.',
    category: 'Practice Management',
  },
  {
    icon: <Zap className="h-6 w-6 text-orange-500" />,
    name: 'Zapier',
    description: `Connect ${appBaseName} to 5,000+ apps with no-code automations.`,
    category: 'Automation',
  },
]

export default function IntegrationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  const brand = useBrand()
  const plannedIntegrations = getPlannedIntegrations(splitBrandName(brand.appName)[0])
  const [nylasStatus, setNylasStatus] = useState<NylasStatus | null>(null)
  const [loadingNylas, setLoadingNylas] = useState(true)
  const [connectingNylas, setConnectingNylas] = useState(false)
  const [disconnectingNylas, setDisconnectingNylas] = useState(false)

  const loadNylasStatus = async () => {
    setLoadingNylas(true)
    try {
      const { data } = await api.get<NylasStatus>('/api/integrations/calendar/status')
      setNylasStatus(data)
    } catch (err: any) {
      console.error('Failed to load Nylas calendar status:', err)
      setNylasStatus({ connected: false })
    } finally {
      setLoadingNylas(false)
    }
  }

  useEffect(() => {
    loadNylasStatus()
  }, [])

  // Handle OAuth redirect result
  useEffect(() => {
    const nylas = searchParams.get('nylas')
    if (!nylas) return

    if (nylas === 'connected') {
      const provider = searchParams.get('provider') || ''
      const friendly = PROVIDER_LABEL[provider] || 'Calendar'
      toast({ title: `${friendly} connected` })
      loadNylasStatus()
    } else if (nylas === 'error') {
      const reason = searchParams.get('reason') || 'unknown error'
      toast({ title: 'Calendar connection failed', description: reason, variant: 'destructive' })
    }

    // Clean query params
    searchParams.delete('nylas')
    searchParams.delete('provider')
    searchParams.delete('reason')
    setSearchParams(searchParams, { replace: true })
  }, [searchParams, setSearchParams])

  const handleNylasConnect = async () => {
    setConnectingNylas(true)
    try {
      const { data } = await api.get<{ authUrl: string }>('/api/integrations/nylas/connect')
      if (data.authUrl) {
        window.location.href = data.authUrl
      } else {
        toast({ title: 'Could not start calendar connection', variant: 'destructive' })
        setConnectingNylas(false)
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Connection failed'
      toast({ title: 'Connection failed', description: msg, variant: 'destructive' })
      setConnectingNylas(false)
    }
  }

  const handleNylasDisconnect = async () => {
    if (!confirm('Disconnect calendar? Future bookings will no longer be added to your calendar.')) return
    setDisconnectingNylas(true)
    try {
      await api.delete('/api/integrations/nylas')
      toast({ title: 'Calendar disconnected' })
      await loadNylasStatus()
    } catch (err: any) {
      toast({ title: 'Disconnect failed', variant: 'destructive' })
    } finally {
      setDisconnectingNylas(false)
    }
  }

  const isNylasConnected = nylasStatus?.connected === true
  const nylasHasError = nylasStatus?.status === 'error'
  const nylasProviderLabel = nylasStatus?.provider
    ? (PROVIDER_LABEL[nylasStatus.provider] || nylasStatus.provider)
    : null

  return (
    <div className="space-y-6 sm:space-y-8">
      <PageHeader
        eyebrow="Connections"
        title="Integrations"
        subtitle="Connect your calendar, CRM, and outbound tools so the AI hands off cleanly."
        icon={
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand-royal/20 to-brand-periwinkle/15 border border-brand-royal/20 flex items-center justify-center">
            <Plug className="h-5 w-5 text-brand-royal" />
          </div>
        }
      />

      {/* Universal Calendar (Nylas) */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-semibold">Available now</h3>
        </div>
        <Card className="card-inset">
          <CardContent className="pt-6 pb-5">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium">Connect any calendar</p>
                      {loadingNylas ? (
                        <Badge variant="secondary" className="text-xs">
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" /> Checking…
                        </Badge>
                      ) : isNylasConnected && !nylasHasError ? (
                        <Badge className="text-xs bg-green-600 hover:bg-green-600">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          {nylasProviderLabel ? `Connected — ${nylasProviderLabel}` : 'Connected'}
                        </Badge>
                      ) : nylasHasError ? (
                        <Badge variant="destructive" className="text-xs">
                          <AlertCircle className="h-3 w-3 mr-1" /> Error
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Not connected</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      One button, every major provider — Google, Microsoft 365 / Outlook, iCloud,
                      Exchange. The AI checks availability before offering times and writes new
                      bookings into your real calendar so it shows up wherever you already work.
                    </p>
                    {isNylasConnected && nylasStatus?.email && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Connected as <span className="font-mono">{nylasStatus.email}</span>
                      </p>
                    )}
                    {nylasHasError && nylasStatus?.lastError && (
                      <p className="text-xs text-destructive mt-2">{nylasStatus.lastError}</p>
                    )}
                  </div>
                  <div className="shrink-0">
                    {isNylasConnected ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNylasDisconnect}
                        disabled={disconnectingNylas}
                      >
                        {disconnectingNylas ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Disconnect'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={handleNylasConnect}
                        disabled={connectingNylas || loadingNylas}
                      >
                        {connectingNylas ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Connect'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Planned Integrations */}
      <div>
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-lg font-semibold">Planned integrations</h3>
          <Plug className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {plannedIntegrations.map((integration) => (
            <Card key={integration.name}>
              <CardContent className="pt-6 pb-5">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                      {integration.icon}
                    </div>
                    <Badge variant="secondary" className="text-xs">Coming soon</Badge>
                  </div>
                  <div>
                    <p className="font-medium text-sm">{integration.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{integration.category}</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug">{integration.description}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
